import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = ["welcome", "followup", "viewing", "pricing", "closing", "general"];

const emptyForm = { name: "", category: "general", subject: "", body: "", variables: [] };

export default function AdminEmailTemplates() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => { api.get("/admin/email-templates").then((r) => setItems(r.data.items || [])); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name || !editing.subject || !editing.body) { toast.error("Name, subject and body are required"); return; }
    if (editing.id) await api.put(`/admin/email-templates/${editing.id}`, editing);
    else await api.post(`/admin/email-templates`, editing);
    toast.success("Template saved");
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/admin/email-templates/${id}`);
    toast.success("Deleted");
    load();
  };

  const inputCls = "w-full border border-[#E5E7EB] px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:border-[#001588]";

  return (
    <div className="p-10 max-w-5xl" data-testid="admin-email-templates">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">CRM</div>
          <h1 className="font-serif text-4xl">Email templates</h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">Reusable emails your agents can send from any lead. Variables like {"{{name}}"} and {"{{listing_title}}"} are auto-filled at send time.</p>
        </div>
        <button
          data-testid="template-new-btn"
          onClick={() => setEditing({ ...emptyForm })}
          className="px-5 py-3 bg-[#001588] text-white text-sm uppercase tracking-[0.2em] hover:bg-black"
        >+ New template</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} className="bg-white border border-[#E5E7EB] p-5" data-testid={`template-row-${t.id}`}>
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500">{t.category}</div>
            <h3 className="font-serif text-xl mt-1">{t.name}</h3>
            <div className="text-sm text-gray-600 mt-1 mb-2">{t.subject}</div>
            <div className="text-xs text-gray-500 whitespace-pre-line line-clamp-3">{t.body}</div>
            <div className="mt-4 flex gap-3">
              <button onClick={() => setEditing({ ...t })} className="text-xs uppercase tracking-wider text-[#001588] hover:underline" data-testid={`template-edit-${t.id}`}>Edit</button>
              <button onClick={() => remove(t.id)} className="text-xs uppercase tracking-wider text-red-600 hover:underline" data-testid={`template-delete-${t.id}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="template-editor">
            <h2 className="font-serif text-2xl mb-4">{editing.id ? "Edit template" : "New template"}</h2>
            <div className="space-y-3">
              <input className={inputCls} placeholder="Template name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="template-input-name" />
              <select className={inputCls} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input className={inputCls} placeholder="Subject" value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} data-testid="template-input-subject" />
              <textarea rows={12} className={`${inputCls} font-mono text-sm`} placeholder="Body (use {{name}}, {{listing_title}}, {{agent_name}} …)" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} data-testid="template-input-body" />
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setEditing(null)} className="px-5 py-2 border border-[#E5E7EB] text-sm">Cancel</button>
                <button onClick={save} className="px-5 py-2 bg-[#001588] text-white text-sm uppercase tracking-[0.2em]" data-testid="template-save">Save template</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
